import { createTheme, Shadows } from "@mui/material/styles";

const lightThemeCustom = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#80CC00",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FFB7A0",
      contrastText: "#000000",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#343A40",
      secondary: "#6C757D",
    },
    divider: "rgba(0, 0, 0, 0.08)",
  },
  typography: {
    fontFamily: "'Roboto', 'Arial', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 400,
    fontWeightBold: 500,
    h1: { color: "#80CC00", fontSize: "1.8rem", fontWeight: 400 },
    h2: { color: "#80CC00", fontSize: "1.5rem", fontWeight: 400 },
    h3: { color: "#80CC00", fontSize: "1.25rem", fontWeight: 400 },
    h4: { color: "#80CC00", fontSize: "1.125rem", fontWeight: 400 },
    h5: { color: "#80CC00" },
    h6: { color: "#80CC00" },
    body1: { fontSize: "0.75rem", fontWeight: 300 },
    body2: { fontSize: "0.75rem", fontWeight: 300 },
    button: { textTransform: "none", fontWeight: 300, fontSize: "0.75rem" },
  },
  shape: {
    borderRadius: 4,
  },
  shadows: Array(25).fill("none") as Shadows,
  components: {
    MuiLink: { defaultProps: { color: "#A0C4FF" } },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "none",
          border: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: 4,
          padding: "8px 20px",
          "&:hover": {
            boxShadow: "none",
          },
        },        containedPrimary: {
          backgroundColor: "#80CC00",
          color: "#000000",
          "&:hover": {
            backgroundColor: "#65A300",
            color: "#000000",
          },
        },        containedSecondary: {
          backgroundColor: "#FFB7A0",
          color: "#000000",
          "&:hover": {
            backgroundColor: "#FFAC90",
            color: "#000000",
          },
        },
        outlinedPrimary: {
          borderColor: "#80CC00",
          color: "#80CC00",
          "&:hover": {
            backgroundColor: "rgba(128, 204, 0, 0.08)",
            borderColor: "#65A300",
          },
        },
        outlinedSecondary: {
          borderColor: "#FFB7A0",
          color: "#FFB7A0",
          "&:hover": {
            backgroundColor: "rgba(255, 183, 160, 0.08)",
            borderColor: "#FFAC90",
          },
        },
        textPrimary: {
          color: "#80CC00",
          "&:hover": {
            backgroundColor: "rgba(128, 204, 0, 0.08)",
          },
        },
        textSecondary: {
          color: "#FFB7A0",
          "&:hover": {
            backgroundColor: "rgba(255, 183, 160, 0.08)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: "none",
          backgroundColor: "#F8F9FA",
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
          // Standardize edit icon colors to blue across all components
          "& .MuiSvgIcon-root": {
            "&[data-testid='EditIcon']": {
              color: "#80CC00", // primary.main color
            },
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:hover": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&:hover": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.925rem", // Increased from 0.875rem or default
          padding: "12px 16px", // Adjusted padding for more space
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        },
        head: {
          color: "#80CC00",
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.15)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.3)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#80CC00",
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          backgroundColor: "rgba(0, 0, 0, 0.03)",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          },
          "&:before": {
            borderBottom: "none !important",
          },
          "&:after": {
            borderBottom: "none !important",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#6C757D",
        },
        outlined: {
          "&.Mui-focused": {
            color: "#80CC00",
          },
        },
      },
    },
  },
});

const themeWithCssVars = {
  ...lightThemeCustom,
  cssVars: {
    "--color-primary": lightThemeCustom.palette.primary.main,
    "--color-secondary": lightThemeCustom.palette.secondary.main,
    "--color-background": lightThemeCustom.palette.background.default,
    "--color-paper": lightThemeCustom.palette.background.paper,
    "--color-text-primary": lightThemeCustom.palette.text.primary,
    "--color-text-secondary": lightThemeCustom.palette.text.secondary,
    "--color-button": lightThemeCustom.palette.primary.main,
  },
};

export default themeWithCssVars;